import { type FC, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { type DropResult } from 'react-beautiful-dnd';

import { Form } from 'react-bootstrap';

import { type RootState } from '@editor2/reducers/root';
import { confirmReplaceFile } from '@editor2/services/apiService';
import { tocFiles } from '@editor2/containers/TableOfContentsPage/styles';
import ErrorBoundary from '@editor2/components/ErrorBoundary';
import UploadArea from './UploadArea';
import {
	updateLectureSlideDeck,
	deleteLectureSlideDeck,
	reorderLectureSlideDeck,
	updateCourseResource,
	incrementActiveFileUploads,
	decrementActiveFileUploads
} from '@editor2/reducers/editor/editorSlice';
import { type CourseFile, type PersistedFile } from './types';
import FilesTable from './FilesTable';
import { useSelectOptions } from './useSelectOptions';
import { useHandleFilesSelected } from './useHandleFilesSelected';
import { useS3Upload } from './useS3Upload';

// ---------------------------------------------------------------------------
// Files – tab content
// ---------------------------------------------------------------------------

interface Props {
	courseId: number;
}

const Files: FC<Props> = ({ courseId }) => {
	const dispatch = useDispatch();
	const { lecture_slide_decks: lectureSlideDeckEntities, book } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	// Track IDs of newly-staged files so they start expanded.
	const expandedIdsRef = useRef(new Set<string>());

	// Initialise local file rows from Redux deck state on mount.
	const [files, setFiles] = useState<CourseFile[]>(() => {
		if (!lectureSlideDeckEntities) return [];
		return lectureSlideDeckEntities.ids.flatMap((lectureSlideDeckFamilyId) => {
			const deck = lectureSlideDeckEntities.entities[lectureSlideDeckFamilyId];
			if (!deck?.file) return [];
			return [
				{
					id: deck.file.family_id,
					fileName: deck.file.original_name,
					associatedFamilyId: deck.associated_family_ids?.[0] ?? '',
					isSelfServeSample: deck.self_serve_sample ?? false,
					lectureSlideDeckFamilyId: deck.family_id,
					filesizeBytes: deck.file.filesize_bytes,
					uploadedAt: deck.file.uploaded_at,
					uploaderName: deck.file.uploader_name,
					notes: deck.file.notes
				} satisfies PersistedFile
			];
		});
	});

	const selectOptions = useSelectOptions(courseId);
	const { handleFilesSelected } = useHandleFilesSelected({ courseId, setFiles, expandedIdsRef });
	const { upload: uploadToS3 } = useS3Upload();

	// Update local state and dispatch Redux update for already-decked files.
	// For newly uploaded files, the deck is already in Redux (dispatched in useFileUpload's
	// onSuccess handler), so handleFileChange only needs to dispatch updateLectureSlideDeck.
	const handleFileChange = useCallback(
		(updated: CourseFile) => {
			setFiles((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
			if (updated.lectureSlideDeckFamilyId) {
				const deck = lectureSlideDeckEntities?.entities[updated.lectureSlideDeckFamilyId];
				dispatch(
					updateLectureSlideDeck({
						courseId,
						lectureSlideDeckFamilyId: updated.lectureSlideDeckFamilyId,
						updates: {
							associated_family_ids: updated.associatedFamilyId ? [updated.associatedFamilyId] : [],
							self_serve_sample: updated.isSelfServeSample,
							...(deck?.file && { file: { ...deck.file, notes: updated.notes ?? '' } })
						}
					})
				);
			}
		},
		[courseId, dispatch, lectureSlideDeckEntities]
	);

	const handleRemove = useCallback(
		(id: string) => {
			// eslint-disable-next-line no-alert
			if (!window.confirm('Are you sure you want to remove this file?')) return;

			const target = files.find((f) => f.id === id);
			if (!target) return;

			if (target.lectureSlideDeckFamilyId) {
				dispatch(
					deleteLectureSlideDeck({
						courseId,
						lectureSlideDeckFamilyId: target.lectureSlideDeckFamilyId
					})
				);
			}

			if (target.upload) {
				// Nothing extra to clean up — the in-flight XHR will complete or error naturally.
			}

			setFiles((prev) => prev.filter((f) => f.id !== id));
		},
		[courseId, dispatch, files]
	);

	// ---- Reorder logic ----
	const handleReorder = useCallback(
		(result: DropResult) => {
			const { source, destination, draggableId } = result;
			if (!destination || destination.index === source.index) return;

			// Update local state
			setFiles((prev) => {
				const updated = [...prev];
				const [moved] = updated.splice(source.index, 1);
				updated.splice(destination.index, 0, moved);
				return updated;
			});

			// Find the deck family ID for the dragged file
			const target = files.find((f) => f.id === draggableId);
			if (target?.lectureSlideDeckFamilyId) {
				dispatch(
					reorderLectureSlideDeck({
						courseId,
						lectureSlideDeckFamilyId: target.lectureSlideDeckFamilyId,
						index: destination.index
					})
				);
			}
		},
		[courseId, dispatch, files]
	);

	// ---- Replace slide logic ----
	const replaceInputRef = useRef<HTMLInputElement>(null);
	const replacingFileIdRef = useRef<string | null>(null);

	const handleReplace = useCallback((id: string) => {
		replacingFileIdRef.current = id;
		if (replaceInputRef.current) {
			replaceInputRef.current.value = '';
			replaceInputRef.current.click();
		}
	}, []);

	const handleReplaceInputChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const selected = e.target.files?.[0];
			if (!selected || !replacingFileIdRef.current) return;

			const targetId = replacingFileIdRef.current;
			replacingFileIdRef.current = null;

			const target = files.find((f) => f.id === targetId);
			if (!target) return;

			// For pending files without a persisted deck (e.g. failed new uploads),
			// remove the old entry and start a fresh upload.
			if (!target.lectureSlideDeckFamilyId) {
				setFiles((prev) => prev.filter((f) => f.id !== targetId));
				handleFilesSelected([selected]);
				return;
			}

			const deckId = target.lectureSlideDeckFamilyId;
			const deck = lectureSlideDeckEntities?.entities[deckId];
			if (!deck?.file) return;

			// Show uploading state
			dispatch(incrementActiveFileUploads({ courseId }));
			setFiles((prev) =>
				prev.map((f) =>
					f.id === targetId ? { ...f, upload: { status: 'uploading' as const, progress: 0 } } : f
				)
			);

			try {
				// Step 1+2: Upload directly to S3 (presigned URL fetched inside the hook)
				const key = await uploadToS3(selected, (pct) => {
					setFiles((prev) =>
						prev.map((f) =>
							f.id === targetId && f.upload ? { ...f, upload: { ...f.upload, progress: pct } } : f
						)
					);
				});

				// Step 3: Confirm the replacement — backend reads all metadata from S3
				const { data } = await confirmReplaceFile(deck.file, key);

				const serverFile = data.file;

				// Update Redux deck with the replacement file returned by the server
				dispatch(
					updateLectureSlideDeck({
						courseId,
						lectureSlideDeckFamilyId: deckId,
						updates: {
							file: serverFile
						}
					})
				);

				// Update local state — clear upload status, update metadata, preserve notes
				setFiles((prev) =>
					prev.map((f): CourseFile => {
						if (f.id !== targetId) return f;
						return {
							id: f.id,
							fileName: serverFile.original_name,
							associatedFamilyId: f.associatedFamilyId,
							isSelfServeSample: f.isSelfServeSample,
							lectureSlideDeckFamilyId: f.lectureSlideDeckFamilyId!,
							filesizeBytes: serverFile.filesize_bytes,
							uploadedAt: serverFile.uploaded_at,
							uploaderName: serverFile.uploader_name,
							notes: f.notes
						} satisfies PersistedFile;
					})
				);
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Upload failed';
				setFiles((prev) =>
					prev.map((f) =>
						f.id === targetId && f.upload
							? { ...f, upload: { ...f.upload, status: 'failed' as const, error: message } }
							: f
					)
				);
			} finally {
				dispatch(decrementActiveFileUploads({ courseId }));
			}
		},
		[courseId, lectureSlideDeckEntities, dispatch, files, handleFilesSelected, uploadToS3]
	);

	return (
		<div className={tocFiles}>
			<div className="filesHeader">
				<h2 className="filesHeading">Lecture Slides</h2>
				<div className="availabilityToggle">
					<Form.Check
						type="switch"
						id="lecture-slides-available"
						label="Assigned files available to instructors in Tools menu"
						checked={book.lecture_slide_decks_available ?? false}
						disabled={files.length === 0}
						onChange={(e) =>
							dispatch(
								updateCourseResource({
									courseId,
									lecture_slide_decks_available: e.target.checked
								})
							)
						}
					/>
					<p className="availabilityNote">
						Self-serve samples will always be available to instructors in the self-serve preview
						regardless of this toggle.
					</p>
				</div>
			</div>
			<input
				ref={replaceInputRef}
				type="file"
				accept=".pptx,.ppt,.pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,application/pdf"
				style={{ display: 'none' }}
				onChange={handleReplaceInputChange}
				tabIndex={-1}
				aria-hidden="true"
			/>
			<FilesTable
				courseId={courseId}
				files={files}
				selectOptions={selectOptions}
				expandedIds={expandedIdsRef.current}
				onChange={handleFileChange}
				onRemove={handleRemove}
				onReplace={handleReplace}
				onReorder={handleReorder}
			/>

			<UploadArea onFilesSelected={handleFilesSelected} />
		</div>
	);
};

const FilesWithBoundary: FC<Props> = (props) => (
	<ErrorBoundary>
		<Files {...props} />
	</ErrorBoundary>
);

export default FilesWithBoundary;
